import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';

const statusOptions = [
  'OPEN',
  'CLOSED',
  'ABSENCE_VACATION',
  'ABSENCE_SICK',
  'ABSENCE_UNPAID',
  'ABSENCE_PERSONAL',
  'ABSENCE_OTHER',
];

const actionOptions = [
  { value: 'ADD_PUNCH', label: 'Agregar punch' },
  { value: 'REMOVE_PUNCH', label: 'Eliminar punch' },
  { value: 'CHANGE_STATUS', label: 'Cambiar estado' },
];

const punchTypes = ['IN', 'OUT'];
const punchSources = ['MANUAL', 'DEVICE', 'SYSTEM'];

const nextId = (items) => items.reduce((max, item) => Math.max(max, item.id), 0) + 1;

export default function AttendanceAdjustments() {
  const today = new Date().toISOString().slice(0, 10);
  const [employees, setEmployees] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState({
    employeeId: '',
    date: today,
    action: 'ADD_PUNCH',
    punchType: 'IN',
    punchedAt: `${today}T08:00`,
    source: 'MANUAL',
    punchId: '',
    status: 'OPEN',
    reason: '',
  });

  useEffect(() => {
    const loadEmployees = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get('/employees');
        setEmployees(response.data);
        if (response.data.length > 0) {
          setForm((prev) => ({ ...prev, employeeId: response.data[0].id }));
        }
      } catch (err) {
        setMessage({
          type: 'error',
          text: err.response?.data?.message || 'No se pudieron cargar los empleados.',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadEmployees();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const findRecord = async (employeeId, date) => {
    const response = await axios.get('/attendance', {
      params: { employeeId, from: date, to: date },
    });
    return response.data[0] || null;
  };

  const ensureRecord = async (employeeId, date) => {
    let record = await findRecord(employeeId, date);
    if (!record) {
      const createResponse = await axios.post(`/attendance/employee/${employeeId}/record`, {
        date,
      });
      record = createResponse.data;
    }
    return record;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setMessage(null);
    const employeeId = Number(form.employeeId);

    if (!employeeId) {
      setMessage({ type: 'error', text: 'Debe seleccionar un empleado.' });
      return;
    }

    try {
      let record = await ensureRecord(employeeId, form.date);

      const payload = {
        action: form.action,
        reason: form.reason || undefined,
      };

      if (form.action === 'ADD_PUNCH') {
        if (!form.punchedAt || !form.punchType) {
          setMessage({ type: 'error', text: 'Falta el tipo o la hora del punch.' });
          return;
        }
        payload.punchType = form.punchType;
        payload.punchedAt = new Date(form.punchedAt).toISOString();
        payload.source = form.source;
      }

      if (form.action === 'REMOVE_PUNCH') {
        if (!form.punchId) {
          setMessage({ type: 'error', text: 'Debe indicar el ID del punch.' });
          return;
        }
        payload.punchId = Number(form.punchId);
      }

      if (form.action === 'CHANGE_STATUS') {
        if (!form.status) {
          setMessage({ type: 'error', text: 'Debe indicar el estado.' });
          return;
        }
        payload.status = form.status;
      }

      const response = await axios.patch(`/attendance/${record.id}/adjust`, payload);
      const adjustment = response.data;

      const newAdjustment = {
        id: adjustment.id || nextId(adjustments),
        createdAt: adjustment.createdAt || new Date().toISOString(),
        employeeId,
        date: form.date,
        action: form.action,
        reason: form.reason || null,
        beforeData: adjustment.beforeData || null,
        afterData: adjustment.afterData || null,
      };

      setAdjustments((prev) => [newAdjustment, ...prev]);
      setMessage({ type: 'success', text: 'Ajuste registrado correctamente.' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'No se pudo registrar el ajuste.',
      });
    }
  };

  const renderConditionalFields = () => {
    if (form.action === 'ADD_PUNCH') {
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-gray-600">Tipo de punch</label>
            <select
              name="punchType"
              value={form.punchType}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            >
              {punchTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600">Hora</label>
            <input
              type="datetime-local"
              name="punchedAt"
              value={form.punchedAt}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600">Origen</label>
            <select
              name="source"
              value={form.source}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            >
              {punchSources.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    }

    if (form.action === 'REMOVE_PUNCH') {
      return (
        <div>
          <label className="text-sm font-semibold text-gray-600">ID del punch</label>
          <input
            type="number"
            name="punchId"
            value={form.punchId}
            onChange={handleChange}
            className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            placeholder="Ej: 1002"
          />
        </div>
      );
    }

    return (
      <div>
        <label className="text-sm font-semibold text-gray-600">Estado</label>
        <select
          name="status"
          value={form.status}
          onChange={handleChange}
          className="mt-1 w-full rounded-lg border border-gray-200 p-2"
        >
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Ajustes de asistencia</h1>
        <p className="text-sm text-gray-500">Registra cambios con before/after en datos mock.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg bg-white p-6 shadow-sm">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-gray-600">Empleado</label>
                <select
                  name="employeeId"
                  value={form.employeeId}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                >
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Fecha</label>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-600">Accion</label>
              <select
                name="action"
                value={form.action}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border border-gray-200 p-2"
              >
                {actionOptions.map((action) => (
                  <option key={action.value} value={action.value}>
                    {action.label}
                  </option>
                ))}
              </select>
            </div>

            {renderConditionalFields()}

            <div>
              <label className="text-sm font-semibold text-gray-600">Motivo</label>
              <textarea
                name="reason"
                value={form.reason}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                rows="3"
                placeholder="Detalle del ajuste"
              />
            </div>

            {message && (
              <div
                className={`rounded-lg px-4 py-2 text-sm ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-600'
                }`}
              >
                {message.text}
              </div>
            )}

            <button
              type="submit"
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
            >
              Guardar ajuste
            </button>
          </form>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800">Ultimos ajustes</h2>
          <p className="text-xs text-gray-500">Se listan en memoria (mock).</p>
          <div className="mt-4 space-y-4">
            {isLoading ? (
              <p className="text-sm text-gray-500">Cargando empleados...</p>
            ) : adjustments.length === 0 ? (
              <p className="text-sm text-gray-500">Sin ajustes registrados.</p>
            ) : (
              adjustments.map((adjustment) => {
                const employee = employees.find((item) => item.id === adjustment.employeeId);
                return (
                  <div key={adjustment.id} className="rounded-lg border border-gray-100 p-3">
                    <p className="text-sm font-semibold text-gray-800">
                      {employee ? employee.name : 'Sin nombre'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {adjustment.date} - {adjustment.action}
                    </p>
                    <p className="text-xs text-gray-500">Motivo: {adjustment.reason || 'N/A'}</p>
                    <details className="mt-2 text-xs text-gray-600">
                      <summary className="cursor-pointer">Before / After</summary>
                      <pre className="mt-2 whitespace-pre-wrap rounded bg-gray-50 p-2 text-[11px]">
                        {JSON.stringify(
                          { before: adjustment.beforeData, after: adjustment.afterData },
                          null,
                          2,
                        )}
                      </pre>
                    </details>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
